import React, { useState } from 'react';
import { Scale, X, ChevronUp, ChevronDown, Save, Trash2, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useComparison } from '@/contexts/ComparisonContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useProperties } from '@/hooks/useProperties';
import { useAuth } from '@/hooks/useAuth';
import { resolveImageUrl } from '@/utils/imageUtils';
import { cn } from '@/lib/utils';

interface ComparisonBarProps {
  className?: string;
}

export function ComparisonBar({ className }: ComparisonBarProps) {
  const {
    comparisonList,
    removeFromComparison,
    clearComparison,
    setShowComparison,
    maxComparisonItems,
    savedComparisons,
    saveComparison,
    loadSavedComparison,
    deleteSavedComparison,
  } = useComparison();
  const { t } = useLanguage();
  const { properties } = useProperties();
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(true);
  const [saveName, setSaveName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  if (comparisonList.length === 0) {
    return null;
  }

  const handleSave = async () => {
    if (!saveName.trim()) return;
    await saveComparison(saveName);
    setSaveName('');
    setShowSaveDialog(false);
  };

  const canCompare = comparisonList.length >= 2;

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40 bg-background border-t shadow-lg transition-transform duration-300',
        !isExpanded && 'translate-y-[calc(100%-48px)]',
        className
      )}
    >
      {/* Toggle bar */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="absolute -top-10 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-2 rounded-t-lg flex items-center gap-2 shadow-lg"
      >
        <Scale className="w-4 h-4" />
        <span className="text-sm font-medium">
          {t('property.compare') || 'Compare'} ({comparisonList.length}/{maxComparisonItems})
        </span>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronUp className="w-4 h-4" />
        )}
      </button>

      <div className="container mx-auto p-4">
        <div className="flex items-center gap-4 overflow-x-auto pb-2">
          {/* Property thumbnails */}
          <div className="flex gap-3 flex-1 min-w-0">
            {comparisonList.map((property) => (
              <div
                key={property.id}
                className="relative flex-shrink-0 group"
              >
                <img
                  src={resolveImageUrl(property.image)}
                  alt={property.address}
                  className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-lg border"
                />
                <button
                  onClick={() => removeFromComparison(property.id)}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
                <p className="text-xs text-center mt-1 max-w-20 truncate">
                  {property.city}
                </p>
              </div>
            ))}

            {/* Empty slots */}
            {Array.from({ length: maxComparisonItems - comparisonList.length }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 border-2 border-dashed border-muted-foreground/30 rounded-lg flex items-center justify-center"
              >
                <span className="text-xs text-muted-foreground">+</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Saved comparisons dropdown */}
            {user && savedComparisons.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <FolderOpen className="w-4 h-4 mr-2" />
                    {t('common.saved') || 'Saved'}
                    <Badge variant="secondary" className="ml-2">
                      {savedComparisons.length}
                    </Badge>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  {savedComparisons.map((saved) => (
                    <DropdownMenuItem
                      key={saved.id}
                      className="flex items-center justify-between"
                    >
                      <button
                        onClick={() => loadSavedComparison(saved, properties)}
                        className="flex-1 text-left"
                      >
                        <span className="font-medium">{saved.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          ({saved.property_ids.length})
                        </span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSavedComparison(saved.id);
                        }}
                        className="p-1 hover:bg-destructive/10 rounded"
                      >
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </button>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Save comparison button */}
            {user && canCompare && (
              <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Save className="w-4 h-4 mr-2" />
                    {t('common.save') || 'Save'}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('property.save_comparison') || 'Save Comparison'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <Input
                      placeholder={t('property.comparison_name') || 'Comparison name'}
                      value={saveName}
                      onChange={(e) => setSaveName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                    />
                    <div className="flex gap-2">
                      <Button onClick={handleSave} disabled={!saveName.trim()}>
                        {t('common.save') || 'Save'}
                      </Button>
                      <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                        {t('common.cancel') || 'Cancel'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* Clear button */}
            <Button variant="ghost" size="sm" onClick={clearComparison}>
              <Trash2 className="w-4 h-4 mr-2" />
              {t('common.clear') || 'Clear'}
            </Button>

            {/* Compare button */}
            <Button
              onClick={() => setShowComparison(true)}
              disabled={!canCompare}
              size="sm"
            >
              <Scale className="w-4 h-4 mr-2" />
              {t('property.compare') || 'Compare'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ComparisonBar;
